import React, { forwardRef } from 'react';

export const Input = forwardRef(({
  label,
  error,
  helperText,
  icon: Icon,
  rightElement,
  className = '',
  id,
  required,
  ...props
}, ref) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        {Icon && (
          <div className="absolute left-3.5 text-slate-400 dark:text-slate-500 pointer-events-none">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          required={required}
          className={`
            w-full bg-white dark:bg-slate-900 
            text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500
            border rounded-xl px-3.5 py-2 text-sm transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-600
            disabled:bg-slate-50 dark:disabled:bg-slate-800/50 disabled:opacity-60 disabled:cursor-not-allowed
            ${Icon ? 'pl-10' : ''}
            ${rightElement ? 'pr-10' : ''}
            ${error 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
              : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}
            ${className}
          `}
          {...props}
        />
        {rightElement && (
          <div className="absolute right-3.5 flex items-center text-slate-400">
            {rightElement}
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-500 font-medium">{error}</p>
      )}
      {!error && helperText && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helperText}</p>
      )}
    </div>
  );
});

export const Select = forwardRef(({
  label,
  error,
  helperText,
  children,
  className = '',
  id,
  required,
  ...props
}, ref) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <select
        ref={ref}
        id={inputId}
        required={required}
        className={`
          w-full bg-white dark:bg-slate-900 
          text-slate-900 dark:text-slate-100
          border rounded-xl px-3.5 py-2 text-sm transition-all duration-150
          focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-600
          disabled:bg-slate-50 dark:disabled:bg-slate-800/50 disabled:opacity-60
          ${error 
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
            : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}
          ${className}
        `}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p className="mt-1 text-xs text-red-500 font-medium">{error}</p>
      )}
      {!error && helperText && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helperText}</p>
      )}
    </div>
  );
});

export const Textarea = forwardRef(({
  label,
  error,
  helperText,
  className = '',
  rows = 3,
  id,
  required,
  ...props
}, ref) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        required={required}
        className={`
          w-full bg-white dark:bg-slate-900 
          text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500
          border rounded-xl px-3.5 py-2 text-sm transition-all duration-150
          focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-600
          disabled:bg-slate-50 dark:disabled:bg-slate-800/50 disabled:opacity-60
          ${error 
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
            : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}
          ${className}
        `}
        {...props}
      />
      {error && (
        <p className="mt-1 text-xs text-red-500 font-medium">{error}</p>
      )}
      {!error && helperText && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helperText}</p>
      )}
    </div>
  );
});
